import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { api, ApiError } from './api.js';

/**
 * Passkey ceremonies, wrapped so screens don't have to know about WebAuthn.
 *
 * The main job here is turning the browser's exceptions into something a human
 * can act on. WebAuthn throws bare DOMExceptions — `NotAllowedError` covers
 * both "the user pressed cancel" and "it timed out", which are very different
 * things to say back to someone.
 */

export const passkeysSupported = (): boolean => browserSupportsWebAuthn();

/** The user dismissed the sheet. Not an error; the UI should just go quiet. */
export class PasskeyCancelled extends Error {
  constructor() {
    super('cancelled');
    this.name = 'PasskeyCancelled';
  }
}

function translate(err: unknown): Error {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) {
    const name = err.name;
    if (name === 'NotAllowedError' || name === 'AbortError') return new PasskeyCancelled();
    if (name === 'InvalidStateError') {
      return new Error('This device already has a passkey for your account.');
    }
    if (name === 'SecurityError') {
      return new Error(
        "Passkeys need a secure connection. They work on localhost, but over your network you'd need HTTPS.",
      );
    }
    if (name === 'NotSupportedError') {
      return new Error("This device can't create a passkey. You can keep using your password.");
    }
    return new Error(err.message || 'Something went wrong with the passkey.');
  }
  return new Error('Something went wrong with the passkey.');
}

/** Add a passkey to the account you are already signed into. */
export async function registerPasskey(label: string | null): Promise<void> {
  try {
    const optionsJSON = (await api.passkeyRegisterOptions()) as Parameters<
      typeof startRegistration
    >[0]['optionsJSON'];
    const response = await startRegistration({ optionsJSON });
    await api.passkeyRegisterVerify(response, label);
  } catch (err) {
    throw translate(err);
  }
}

/**
 * Sign in with a passkey. No email needed — the authenticator offers whatever
 * it holds for this site, which also means the server reveals nothing about
 * which accounts exist.
 */
export async function signInWithPasskey(): Promise<void> {
  try {
    const optionsJSON = (await api.passkeyLoginOptions()) as Parameters<
      typeof startAuthentication
    >[0]['optionsJSON'];
    const response = await startAuthentication({ optionsJSON });
    await api.passkeyLoginVerify(response);
  } catch (err) {
    throw translate(err);
  }
}
